<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { FreeMode } from "swiper/modules";
import type { Swiper as SwiperInstance } from "swiper/types";
import InteractieveKaartDetailCard from "@/views/InteractieveKaart/components/InteractieveKaartDetailCard.vue";

type OverlayPinType = "aanbieder" | "activiteit" | "speelplek" | "wandelroute" | "fietsroute";

interface InteractieveKaartOverlayPin {
	id: number;
	title: string;
	type: OverlayPinType;
	latitude: number;
	longitude: number;
	kind: "activity" | "play";
	gpxUrl?: string;
	gpxXml?: string;
	sportmomentId?: number;
	aanbiederId?: string;
	address?: string;
	description?: string;
	isLoadingDetails?: boolean;
	imageUrl?: string;
	infoUrl?: string;
	bookingUrl?: string;
	externalBookingUrl?: string;
	waitlistUrl?: string;
	bookingType?: number;
	availableSpots?: number;
	isSamePlatform?: boolean;
	showBookButton?: boolean;
	showWaitlist?: boolean;
	displayLabel?: string;
	routeDistance?: number | null;
	routeInspanning?: string[];
	routeTerreintype?: string[];
}

const props = defineProps<{
	pins: any[];
	selectedPinId: number | null;
	pinColors: Record<OverlayPinType, string>;
	pinIcons: Record<OverlayPinType, string>;
}>();

const emit = defineEmits<{
	(e: "select-pin", pinId: number | null): void;
	(e: "focus-pin", pinId: number): void;
	(e: "hover-pin", pinId: number | null): void;
}>();

const MAX_OVERLAY_PIN_LIMIT = 20;

const openedCardPinId = ref<number | null>(null);
const swiperInstance = ref<SwiperInstance | null>(null);
const swiperModules = [FreeMode];
const showRightFade = ref(false);
const hasOpenCard = ref(false);

const isMobile = ref(false);
const isSheetExpanded = ref(false);
let mediaQueryList: MediaQueryList | null = null;
let handleTouchStartY = 0;
const mobileCardRefs: Record<number, HTMLElement> = {};

const cappedPins = computed<any[]>(() => {
	if (props.pins.length <= MAX_OVERLAY_PIN_LIMIT) {
		return props.pins;
	}

	const capped = props.pins.slice(0, MAX_OVERLAY_PIN_LIMIT);
	const selectedPinId = props.selectedPinId;
	if (!selectedPinId) {
		return capped;
	}

	const alreadyIncluded = capped.some((pin) => pin.id === selectedPinId);
	if (alreadyIncluded) {
		return capped;
	}

	const selectedPin = props.pins.find((pin) => pin.id === selectedPinId);
	if (!selectedPin || capped.length === 0) {
		return capped;
	}

	// Keep memory bounded while guaranteeing the selected map pin is visible in the cards.
	const nextCapped = [...capped];
	nextCapped[nextCapped.length - 1] = selectedPin;
	return nextCapped;
});

const visiblePins = computed<any[]>(() => cappedPins.value);

const setMobileCardRef = (pinId: number, el: HTMLElement | null): void => {
	if (el) {
		mobileCardRefs[pinId] = el;
	} else {
		delete mobileCardRefs[pinId];
	}
};

const scrollMobileCardIntoView = (pinId: number): void => {
	const el = mobileCardRefs[pinId];
	if (!el) return;
	el.scrollIntoView({ behavior: "smooth", block: "nearest" });
};

const scrollCardIntoView = (pinId: number): void => {
	const swiper = swiperInstance.value;
	if (!swiper) {
		return;
	}

	const pinIndex = visiblePins.value.findIndex((pin) => pin.id === pinId);
	if (pinIndex === -1) {
		return;
	}

	swiper.slideTo(pinIndex, 350);
};

const updateRightFade = (swiperArg?: SwiperInstance): void => {
	const swiper = swiperArg ?? swiperInstance.value;
	if (!swiper) {
		showRightFade.value = false;
		return;
	}

	showRightFade.value = !swiper.isEnd && !swiper.isLocked;
};

const onSwiperReady = (swiper: SwiperInstance): void => {
	swiperInstance.value = swiper;
	updateRightFade(swiper);
};

const setSwiperDrag = (enabled: boolean): void => {
	const swiper = swiperInstance.value;
	if (!swiper) {
		return;
	}

	swiper.allowTouchMove = enabled;
};

const onMediaQueryChange = (e: MediaQueryListEvent): void => {
	isMobile.value = e.matches;
};

const onHandleTouchStart = (event: TouchEvent): void => {
	handleTouchStartY = event.touches[0].clientY;
};

const onHandleTouchEnd = (event: TouchEvent): void => {
	const deltaY = handleTouchStartY - event.changedTouches[0].clientY;
	if (deltaY > 30) {
		isSheetExpanded.value = true;
	} else if (deltaY < -30) {
		isSheetExpanded.value = false;
	}
};


const onCardToggle = (pinId: number): void => {
	const isOpen = openedCardPinId.value === pinId;
	openedCardPinId.value = isOpen ? null : pinId;
	hasOpenCard.value = !isOpen;
	setSwiperDrag(isOpen);
	emit("select-pin", isOpen ? null : pinId);
};

onMounted(() => {
	mediaQueryList = window.matchMedia("(max-width: 991px)");
	isMobile.value = mediaQueryList.matches;
	mediaQueryList.addEventListener("change", onMediaQueryChange);
});

onUnmounted(() => {
	mediaQueryList?.removeEventListener("change", onMediaQueryChange);
});

watch(() => props.selectedPinId, async (pinId) => {
	if (!pinId) {
		openedCardPinId.value = null;
		hasOpenCard.value = false;
		setSwiperDrag(true);
		await nextTick();
		updateRightFade();
		return;
	}

	openedCardPinId.value = pinId;
	hasOpenCard.value = true;
	setSwiperDrag(false);
	await nextTick();
	if (isMobile.value) {
		isSheetExpanded.value = true;
		scrollMobileCardIntoView(pinId);
	} else {
		scrollCardIntoView(pinId);
	}
	updateRightFade();
});

watch(() => props.pins, async (newPins, oldPins) => {
	if (openedCardPinId.value !== null && !newPins.some((pin) => pin.id === openedCardPinId.value)) {
		openedCardPinId.value = null;
		hasOpenCard.value = false;
		setSwiperDrag(true);
		emit("select-pin", null);
	}

	await nextTick();
	updateRightFade();
}, { deep: false });

const getDisplayLabel = (pin: InteractieveKaartOverlayPin): string => pin.displayLabel ?? (pin.type === "speelplek" ? "Speelplek" : pin.type === "wandelroute" ? "Wandelroute" : pin.type === "fietsroute" ? "Fietsroute" : "Activiteit");

const getActivityIconClass = (pin: InteractieveKaartOverlayPin): string => pin.kind === "activity" ? "icon-agenda" : props.pinIcons[pin.type];

const getPinColor = (pinType: string): string => {
	return props.pinColors[pinType as OverlayPinType];
};
</script>

<template>
	<template v-if="visiblePins.length > 0">

		<!-- Mobile: vertical bottom sheet list -->
		<template v-if="isMobile">
			<div class="interactieve-kaart__mobile-sheet"
				:class="{ 'is-expanded': isSheetExpanded, 'has-open-card': openedCardPinId !== null }"
			>
				<div class="interactieve-kaart__mobile-sheet-handle"
					@click="isSheetExpanded = !isSheetExpanded"
					@touchstart.passive="onHandleTouchStart"
					@touchend.passive="onHandleTouchEnd"
				>
					<span class="interactieve-kaart__mobile-sheet-bar"></span>
				</div>
				<div class="interactieve-kaart__mobile-sheet-list">
					<div
						v-for="pin in visiblePins"
						:key="pin.id"
						:ref="(el) => setMobileCardRef(pin.id, el as HTMLElement | null)"
					>
						<InteractieveKaartDetailCard
							:pin="{ ...pin, displayLabel: getDisplayLabel(pin) }"
							:is-open="openedCardPinId === pin.id"
							:is-active="selectedPinId === pin.id"
							:is-mobile="true"
							:pin-color="getPinColor(pin.type)"
							:icon-class="getActivityIconClass(pin)"
							:address="pin.address"
							:description="pin.description"
							:is-loading-details="pin.isLoadingDetails"
							:image-url="pin.imageUrl"
							:info-url="pin.infoUrl"
							:route-distance="pin.routeDistance"
							:route-inspanning="pin.routeInspanning"
							:route-terreintype="pin.routeTerreintype"
							:booking-url="pin.bookingUrl"
							:external-booking-url="pin.externalBookingUrl"
							:waitlist-url="pin.waitlistUrl"
							:show-book-button="pin.showBookButton"
							:show-waitlist="pin.showWaitlist"
							@toggle="onCardToggle(pin.id)"
							@focus-pin="emit('focus-pin', pin.id)"
						/>
					</div>
				</div>
			</div>
		</template>

		<!-- Desktop: horizontal swiper -->
		<aside
			v-else
			class="interactieve-kaart__overlay"
			:class="{ 'has-right-fade': showRightFade, 'has-open-card': hasOpenCard }"
		>
			<Swiper
				class="interactieve-kaart__overlay-cards"
				:modules="swiperModules"
				:slides-per-view="'auto'"
				:space-between="12"
				:grab-cursor="true"
				:watch-overflow="true"
				:free-mode="{ enabled: true, momentum: true, momentumRatio: 0.7, momentumVelocityRatio: 0.85 }"
				:observe-parents="true"
				:observer="true"
				@swiper="onSwiperReady"
				@progress="updateRightFade"
				@resize="updateRightFade"
			>
				<SwiperSlide
					v-for="pin in visiblePins"
					:key="pin.id"
					class="interactieve-kaart__overlay-card"
					@mouseenter="emit('hover-pin', pin.id)"
					@mouseleave="emit('hover-pin', null)"
				>
					<InteractieveKaartDetailCard
						:pin="{ ...pin, displayLabel: getDisplayLabel(pin) }"
						:is-open="openedCardPinId === pin.id"
						:is-active="selectedPinId === pin.id"
						:is-mobile="false"
						:pin-color="getPinColor(pin.type)"
						:icon-class="getActivityIconClass(pin)"
						:address="pin.address"
						:description="pin.description"
						:is-loading-details="pin.isLoadingDetails"
						:image-url="pin.imageUrl"
						:info-url="pin.infoUrl"
						:route-distance="pin.routeDistance"
						:route-inspanning="pin.routeInspanning"
						:route-terreintype="pin.routeTerreintype"
						:booking-url="pin.bookingUrl"
						:external-booking-url="pin.externalBookingUrl"
						:waitlist-url="pin.waitlistUrl"
						:show-book-button="pin.showBookButton"
						:show-waitlist="pin.showWaitlist"
						@toggle="onCardToggle(pin.id)"
						@focus-pin="emit('focus-pin', pin.id)"
					/>
				</SwiperSlide>
			</Swiper>
		</aside>

	</template>
</template>
